import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from './../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import useSearchMutaion from '../../../../hooks/apis/useSearchMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util.js';
import { HAZ_BASIC_02_Schema } from './HAZ_BASIC_02_Schema';

/**
 * 유해위험기계기구(작업별) 기본정보(HAZ_BASIC_02) 관련 React Query 훅 모음
 * - 기본 조회(useQuery): useSearch1
 * - 토글/탭 조회(useMutation 기반): useSearch2, useSearch3
 * - 삭제/저장(useMutation 기반): useDelete1, useSave1, useSave2, useSave3
 *
 * 만약 MUTATION 훅에서 공통 응답 처리 외에 추가 성공 콜백이 필요하면
 * successOptions 파라미터로 onSuccess 콜백 함수를 넘겨줄 수 있다.
 */

/**
 * useSearch1 - 기본 조회 (useQuery 기반)
 */
export const useSearch1 = (options = {}) => {
  const { onSuccess } = options;
  const params = mapParamsBySchema(HAZ_BASIC_02_Schema.mapper.search1);

  const query = useQuery({
    queryKey: ['HazardousMachines', 'getHazBasic02R2', params],
    queryFn: () =>
      apiRequest('post', Global.urls.HazardousMachines.getHazBasic02R2, params),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/**
 * useSearch2 - 토글 상태 조회 (useMutation 기반)
 */
export const useSearch2 = (options = {}) => {
  const { onSuccess } = options;
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(HAZ_BASIC_02_Schema.mapper.search2, context);
    return apiRequest('post', Global.urls.HazardousMachines.getHazBasic02R0, params);
  }, {
    onSuccess: async (rows) => {
      await onSuccess?.(rows);
    },
  });
};

/**
 * useSearch3 - 탭 데이터 조회 (useMutation 기반)
 */
export const useSearch3 = (options = {}) => {
  const { onSuccess } = options;
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(HAZ_BASIC_02_Schema.mapper.search3, context);
    return apiRequest('post', Global.urls.HazardousMachines.getHazBasic02R1, params);
  }, {
    onSuccess: async (rows) => {
      await onSuccess?.(rows);
    },
  });
};

export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, HAZ_BASIC_02_Schema.mapper.delete1);

    return apiRequest(
      'post1',
      Global.urls.HazardousMachines.deleteHazBasic02D1,
      payload
    );
  }, options);
};

/**
 * useSave1 - 기본 저장 (useMutation 기반)
 */
export const useSave1 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, HAZ_BASIC_02_Schema.mapper.save1);

    return apiRequest(
      'post1',
      Global.urls.HazardousMachines.saveHazBasic02U1,
      payload
    );
  }, options);
};

/**
 * useSave2 - 토글 상태 저장 (useMutation 기반)
 */
export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, HAZ_BASIC_02_Schema.mapper.save2);

    return apiRequest(
      'post1',
      Global.urls.HazardousMachines.saveHazBasic02U0,
      payload
    );
  }, options);
};
